import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { buildHelpTopics } from './buildHelpTopics';
import { SizeGuideContent } from '../Product/SizeGuide';
import './HelpCenter.css';

/**
 * Shared HelpCenter Size Guide page (all brands).
 *
 * Renders the HelpCenter shell (title, lede, sidebar) with the same size-guide
 * content markup as the product side panel (`.sg-*` from Product/SizeGuide.css),
 * so drawer and page stay visually in sync.
 *
 * @param {Component} Header    - Brand header wrapper ({Brand}/Common/Header)
 * @param {Component} Footer    - Brand footer wrapper ({Brand}/Common/Footer)
 * @param {string}    brand     - data-brand attribute value ('CLS' | 'ABC')
 * @param {string}    title     - Page title (SIZE_GUIDE_TITLE)
 * @param {string}    lede      - Intro paragraph under the title
 * @param {Array}     items     - SIZE_GUIDE_ITEMS [{ name, image, sizes }]
 * @param {Array}     categories- FAQ categories (for sidebar topics)
 * @param {Object}    labels    - HELP_LABELS
 * @param {Object}    routes    - HELP_ROUTES
 */
const SizeGuidePage = ({
  Header,
  Footer,
  brand,
  title,
  lede,
  items,
  categories,
  labels,
  routes,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', brand);
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, [brand]);

  const topics = buildHelpTopics({
    context: 'sizeGuide',
    categories,
    labels,
    routes,
    navigate,
  });

  return (
    <div className="helpcenter">
      <Header showTopBar={false} />
      <main className="helpcenter__main">
        <div className="helpcenter__container">
          <div className="helpcenter__list-col">
            <h1 className="helpcenter__title typo-h1">{title}</h1>
            {lede ? <p className="helpcenter__lede typo-body-lg">{lede}</p> : null}
            <SizeGuideContent items={items} />
          </div>

          <Sidebar
            title={labels.sidebarTitle}
            topics={topics}
            activeId="sizeGuide"
            dividerBeforeId={labels.claims && routes.claims ? 'claims' : 'contact'}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
};

SizeGuidePage.propTypes = {
  Header: PropTypes.elementType.isRequired,
  Footer: PropTypes.elementType.isRequired,
  brand: PropTypes.oneOf(['CLS', 'ABC']).isRequired,
  title: PropTypes.string.isRequired,
  lede: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      sizes: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          values: PropTypes.string.isRequired,
        }),
      ).isRequired,
    }),
  ).isRequired,
  categories: PropTypes.array.isRequired,
  labels: PropTypes.object.isRequired,
  routes: PropTypes.object.isRequired,
};

export default SizeGuidePage;
